/**
 * providers/mockData.ts — deterministic placeholder-data generation.
 *
 * Mock providers must obey the same architecture rules as real ones:
 *   • NO hardcoded city logic — everything derives from the location generically.
 *   • NO hardcoded recommendations — every item is GENERATED from neutral name
 *     pools + a seeded PRNG, so identical inputs always yield identical output
 *     (stable for caching, tests, and UI), but no city has special-cased data.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type {
  Recommendation,
  RecommendationAttributes,
  RecommendationCategory,
} from '../types';

// ─── Seeded PRNG (xmur3 hash → mulberry32) ────────────────────────────────────

function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic RNG for a string seed. */
export function seededRng(seed: string): () => number {
  return mulberry32(xmur3(seed)());
}

/**
 * Day index in the location's rough local time — the single source of truth for
 * daily rotation AND the engine's daily cache-invalidation bucket, so the two
 * always agree. Local offset is approximated from longitude (15° ≈ 1 hour);
 * good enough for deterministic placeholder rotation. Real providers can key on
 * the IANA `location.timezone` instead.
 */
export function localDayIndex(location: RoamerLocation, now: number): number {
  const offsetMs = (location.longitude / 15) * 3600_000;
  return Math.floor((now + offsetMs) / 86_400_000);
}

export const between = (rng: () => number, min: number, max: number) =>
  min + rng() * (max - min);

export const intBetween = (rng: () => number, min: number, max: number) =>
  Math.floor(between(rng, min, max + 1));

export const pick = <T,>(rng: () => number, arr: readonly T[]): T =>
  arr[Math.floor(rng() * arr.length)];

export const chance = (rng: () => number, p: number) => rng() < p;

// ─── Neutral name pools (generic building blocks, not city data) ──────────────

const NAME_POOLS: Record<RecommendationCategory, { first: readonly string[]; second: readonly string[] }> = {
  housing: {
    first: ['Riverside', 'Garden', 'Skyline', 'Old Town', 'Central', 'Sunset', 'Palm', 'Harbor'],
    second: ['Loft', 'Residences', 'Studio Suites', 'Apartments', 'House', 'Flats', 'Villas'],
  },
  coworking: {
    first: ['Nomad', 'Latitude', 'Anchor', 'Meridian', 'Signal', 'Compass', 'Junction'],
    second: ['Hub', 'Works', 'Commons', 'Collective', 'Space', 'Studio', 'Society'],
  },
  cafe: {
    first: ['Slow Morning', 'North Bean', 'Paper Cup', 'Little Fern', 'Golden Hour', 'Backstreet', 'Wander'],
    second: ['Roasters', 'Coffee', 'Brew Bar', 'Espresso Room', 'Café', 'Coffee Lab'],
  },
  restaurant: {
    first: ['The Corner', 'Mama\u2019s', 'Night Garden', 'Green Papaya', 'Charcoal', 'Two Spoons', 'Lantern'],
    second: ['Kitchen', 'Table', 'Bistro', 'Eatery', 'Canteen', 'Grill', 'House'],
  },
  neighborhood: {
    first: ['North', 'Riverside', 'Old Town', 'Garden', 'Arts', 'Harbor', 'Hillside', 'Market'],
    second: ['Quarter', 'District', 'Village', 'Side', 'End', 'Commons'],
  },
  transportation: {
    first: ['Metro', 'City', 'Airport', 'Cross-Town', 'Riverside', 'Express'],
    second: ['Line', 'Bikes', 'Shuttle', 'Tram', 'Bus Network', 'Ride Pass'],
  },
  utility: {
    first: ['Local SIM &', 'Laundry &', 'Grocery', 'Fitness', 'Mobile Data', 'Mail &'],
    second: ['Data Plan', 'Cleaning Service', 'Delivery', 'Day Pass', 'Top-Up Kiosk', 'Package Point'],
  },
  emergency: {
    first: ['General', 'Central', 'International', 'Tourist', '24h', 'District'],
    second: ['Hospital', 'Clinic', 'Pharmacy', 'Police Desk', 'Urgent Care', 'Medical Center'],
  },
  community: {
    first: ['Nomad', 'Expat', 'Founders', 'Language', 'Weekend', 'Creative'],
    second: ['Meetup', 'Social', 'Dinner Club', 'Exchange', 'Hikers', 'Coworking Night'],
  },
};

// ─── Attribute generation per category ────────────────────────────────────────

function baseAttributes(rng: () => number): RecommendationAttributes {
  return {
    rating: Math.round(between(rng, 3.4, 4.9) * 10) / 10,
    reviewCount: intBetween(rng, 12, 640),
    distanceKm: Math.round(between(rng, 0.3, 9) * 10) / 10,
    safety: intBetween(rng, 55, 97),
    walkabilityMeters: intBetween(rng, 200, 3500),
    englishFriendly: intBetween(rng, 40, 95),
  };
}

function categoryAttributes(rng: () => number, category: RecommendationCategory): RecommendationAttributes {
  const a = baseAttributes(rng);
  switch (category) {
    case 'housing':
      return {
        ...a,
        monthlyCost: intBetween(rng, 350, 2400),
        internetMbps: intBetween(rng, 20, 500),
        quietness: intBetween(rng, 30, 95),
        nearCoworkingMeters: intBetween(rng, 150, 3000),
        petFriendly: chance(rng, 0.45),
        longStayFriendly: chance(rng, 0.7),
        hasAirConditioning: chance(rng, 0.8),
      };
    case 'coworking':
      return {
        ...a,
        monthlyCost: intBetween(rng, 60, 420),
        internetMbps: intBetween(rng, 80, 900),
        quietness: intBetween(rng, 40, 90),
        open24h: chance(rng, 0.4),
        hasAirConditioning: true,
        hasPowerOutlets: true,
        amenities: chance(rng, 0.5) ? ['Phone Booths', 'Community Events'] : ['Meeting Rooms'],
      };
    case 'cafe':
      return {
        ...a,
        internetMbps: intBetween(rng, 10, 250),
        quietness: intBetween(rng, 25, 92),
        priceLevel: intBetween(rng, 1, 3) as 1 | 2 | 3,
        hasPowerOutlets: chance(rng, 0.7),
        hasAirConditioning: chance(rng, 0.6),
        longStayFriendly: chance(rng, 0.55),
      };
    case 'restaurant':
      return {
        ...a,
        priceLevel: intBetween(rng, 1, 4) as 1 | 2 | 3 | 4,
        petFriendly: chance(rng, 0.3),
      };
    case 'neighborhood':
      return {
        ...a,
        internetMbps: intBetween(rng, 30, 400),
        monthlyCost: intBetween(rng, 400, 2000), // typical housing cost in the area
        nightlife: intBetween(rng, 10, 95),
        natureAccess: intBetween(rng, 15, 95),
        beachAccessKm: Math.round(between(rng, 0.5, 120) * 10) / 10,
        transitScore: intBetween(rng, 25, 95),
        quietness: intBetween(rng, 20, 95),
      };
    case 'transportation':
      return {
        ...a,
        transitScore: intBetween(rng, 40, 98),
        priceLevel: intBetween(rng, 1, 2) as 1 | 2,
      };
    case 'utility':
      return { ...a, priceLevel: intBetween(rng, 1, 3) as 1 | 2 | 3 };
    case 'emergency':
      return { ...a, open24h: chance(rng, 0.65), englishFriendly: intBetween(rng, 50, 98) };
    case 'community':
      return { ...a, nightlife: intBetween(rng, 20, 80), englishFriendly: intBetween(rng, 60, 98) };
    default:
      return a;
  }
}

function deriveTags(attrs: RecommendationAttributes): string[] {
  const tags: string[] = [];
  if ((attrs.internetMbps ?? 0) >= 100) tags.push('Fast WiFi');
  if ((attrs.quietness ?? 0) >= 70) tags.push('Quiet');
  if (attrs.hasPowerOutlets) tags.push('Power Outlets');
  if (attrs.hasAirConditioning) tags.push('Air Conditioning');
  if (attrs.open24h) tags.push('24-Hour Access');
  if (attrs.longStayFriendly) tags.push('Long-Stay Friendly');
  if (attrs.petFriendly) tags.push('Pet Friendly');
  if ((attrs.walkabilityMeters ?? Infinity) <= 900) tags.push('Walkable');
  if ((attrs.safety ?? 0) >= 80) tags.push('Safe Area');
  if ((attrs.transitScore ?? 0) >= 75) tags.push('Great Transit');
  if ((attrs.rating ?? 0) >= 4.5) tags.push('Highly Rated');
  return tags.slice(0, 5);
}

// ─── Public generator ─────────────────────────────────────────────────────────

/**
 * Generate `limit` deterministic placeholder recommendations for a location +
 * category. Same inputs → same outputs, but no city is special-cased.
 */
export function generatePlaceholderRecommendations(
  location: RoamerLocation,
  category: RecommendationCategory,
  limit: number,
  providerId: string,
): Recommendation[] {
  const out: Recommendation[] = [];
  const pool = NAME_POOLS[category];
  const seen = new Set<string>();

  for (let i = 0; i < limit; i++) {
    const rng = seededRng(`${location.locationId}:${category}:${i}`);
    let title = `${pick(rng, pool.first)} ${pick(rng, pool.second)}`;
    if (seen.has(title)) title = `${title} ${String.fromCharCode(65 + i)}`; // de-dupe: "… B"
    seen.add(title);

    const attributes = categoryAttributes(rng, category);
    out.push({
      id: `${providerId}:${location.locationId}:${category}:${i}`,
      category,
      title,
      subtitle: location.city ? `${location.city}` : undefined,
      attributes,
      tags: deriveTags(attributes),
      location: {
        latitude: location.latitude + between(rng, -0.03, 0.03),
        longitude: location.longitude + between(rng, -0.03, 0.03),
      },
      providerId,
    });
  }
  return out;
}
